/**
 * RelacaoPreVendaPage Component (PreVendasPage)
 * 
 * Página de listagem e gerenciamento de pré-vendas (Relação de Pré-Venda).
 * Permite visualizar, confirmar, cancelar e converter pré-vendas.
 * 
 * Funcionalidades:
 * - Listagem de pré-vendas com paginação
 * - Filtros por status, cliente, data, vendedor
 * - Visualização de detalhes
 * - Confirmação de pré-venda (reserva estoque, não baixa)
 * - Cancelamento
 * - Conversão para venda normal
 * - Avisos visuais sobre reserva de estoque
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Eye, X, CheckCircle, RefreshCw, Search, Filter, Calendar, User, AlertCircle } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import { saleService, customerService } from '../../../../services/erpApi'
import './PreVendasPage.css'

function PreVendasPage() {
  const [preVendas, setPreVendas] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedPreVenda, setSelectedPreVenda] = useState(null)
  const [clientes, setClientes] = useState([])
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    customerId: '',
    dateFrom: '',
    dateTo: ''
  })
  const [searchInput, setSearchInput] = useState('') // Estado separado para o input de pesquisa
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  const searchTimeoutRef = useRef(null)
  const searchInputRef = useRef(null) // Ref para o input de pesquisa
  const lastInputTimeRef = useRef(Date.now()) // Para detectar código de barras
  const inputTimesRef = useRef([]) // Array de timestamps das últimas entradas para detectar código de barras

  // Carregar clientes apenas uma vez
  useEffect(() => {
    loadClientes()
  }, [])

  // Focar no campo de pesquisa quando a página carregar
  useEffect(() => {
    // Aguardar um pouco para garantir que o DOM está pronto
    const timer = setTimeout(() => {
      if (searchInputRef.current && !detailModalOpen) {
        searchInputRef.current.focus()
      }
    }, 300)
    
    return () => clearTimeout(timer)
  }, [detailModalOpen]) // Executar quando detailModalOpen mudar

  // Listener para manter foco no campo quando clicar fora (mas não em outros inputs)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Se clicar fora e não for em um input/textarea/select/button, focar no campo de pesquisa
      if (searchInputRef.current && !detailModalOpen) {
        const target = e.target
        const isInteractiveElement = target.tagName === 'INPUT' ||
                                     target.tagName === 'TEXTAREA' ||
                                     target.tagName === 'SELECT' ||
                                     target.tagName === 'BUTTON' ||
                                     target.closest('button') ||
                                     target.closest('a') ||
                                     target.closest('.modal')
        
        if (!isInteractiveElement && document.activeElement !== searchInputRef.current) {
          setTimeout(() => {
            if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
              searchInputRef.current.focus()
            }
          }, 100)
        }
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [detailModalOpen])

  // Garantir que o campo de pesquisa mantenha o foco após carregar dados
  useEffect(() => {
    // Não focar se houver modal aberto ou se já houver outro elemento focado
    if (detailModalOpen) return
    
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        const activeElement = document.activeElement
        // Só focar se não houver outro input/textarea/select focado
        const isInputFocused = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.tagName === 'BUTTON'
        )
        
        if (!isInputFocused) {
          searchInputRef.current.focus()
        }
      }
    }, 150)
    
    return () => clearTimeout(timer)
  }, [loading, detailModalOpen]) // Executar quando loading mudar ou modal fechar

  // Debounce para o campo de pesquisa com detecção de código de barras
  useEffect(() => {
    // Limpar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Se o campo estiver vazio, limpar filtro e não fazer nada
    if (!searchInput.trim()) {
      setFilters(prev => ({ ...prev, search: '' }))
      return
    }

    const now = Date.now()
    
    // Detectar código de barras analisando a velocidade de digitação
    // Códigos de barras são lidos muito rapidamente (todos os caracteres em menos de 200ms)
    const recentInputs = inputTimesRef.current.filter(time => now - time < 200)
    const avgTimeBetweenInputs = recentInputs.length > 1 
      ? (recentInputs[recentInputs.length - 1] - recentInputs[0]) / (recentInputs.length - 1)
      : 1000
    
    // Detectar código de barras:
    // - Mais de 8 caracteres E velocidade média entre caracteres < 20ms (muito rápido)
    // OU
    // - Mais de 13 caracteres (tamanho típico de EAN-13)
    const isLikelyBarcode = (searchInput.length >= 8 && avgTimeBetweenInputs < 20) || 
                            searchInput.length >= 13

    // Se for código de barras, executar busca imediatamente
    if (isLikelyBarcode) {
      setFilters(prev => ({ ...prev, search: searchInput }))
      setPagination(prev => ({ ...prev, page: 1 }))
      // Manter foco no campo após busca e selecionar texto para próxima leitura
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
          searchInputRef.current.select()
        }
      }, 50)
    } else {
      // Para digitação normal, usar debounce de 500ms
      searchTimeoutRef.current = setTimeout(() => {
        setFilters(prev => ({ ...prev, search: searchInput }))
        setPagination(prev => ({ ...prev, page: 1 }))
      }, 500)
    }

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchInput])

  const loadPreVendas = useCallback(async () => {
    setLoading(true)
    try {
      const response = await saleService.getAll({
        ...filters,
        type: 'presale',
        page: pagination.page,
        limit: pagination.limit
      })
      
      if (response.success) {
        setPreVendas(response.data || [])
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          }))
        }
      }
    } catch (error) {
      console.error('Erro ao carregar pré-vendas:', error)
    } finally {
      setLoading(false)
      // Garantir foco no campo após carregar dados
      setTimeout(() => {
        if (searchInputRef.current && !detailModalOpen) {
          const activeElement = document.activeElement
          const isInputFocused = activeElement && (
            activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'SELECT'
          )
          
          if (!isInputFocused) {
            searchInputRef.current.focus()
          }
        }
      }, 300)
    }
  }, [filters, pagination.page, pagination.limit, detailModalOpen])

  // Carregar pré-vendas quando filters ou pagination mudarem (mas não quando searchInput mudar)
  useEffect(() => {
    loadPreVendas()
  }, [loadPreVendas])

  const loadClientes = async () => {
    try {
      const response = await customerService.getAll({ status: 'active' })
      if (response.success) {
        setClientes(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
    }
  }

  const handleViewDetails = async (preVenda) => {
    try {
      const response = await saleService.getById(preVenda.id)
      if (response.success) {
        setSelectedPreVenda(response.data)
        setDetailModalOpen(true)
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error)
      alert('Erro ao carregar detalhes da pré-venda')
    }
  }

  const handleConfirm = async (id) => {
    if (!window.confirm('Deseja confirmar esta pré-venda?\n\n⚠️ ATENÇÃO: Pré-vendas RESERVAM o estoque ao serem confirmadas, mas não baixam. O estoque só será baixado quando convertida em venda.')) {
      return
    }

    try {
      const response = await saleService.confirm(id)
      if (response.success) {
        alert('Pré-venda confirmada com sucesso!')
        loadPreVendas()
        if (detailModalOpen) {
          setDetailModalOpen(false)
        }
      }
    } catch (error) {
      console.error('Erro ao confirmar pré-venda:', error)
      alert(error.response?.data?.message || 'Erro ao confirmar pré-venda')
    }
  }

  const handleCancel = async (id) => {
    if (!window.confirm('Deseja cancelar esta pré-venda?')) {
      return
    }

    try {
      const response = await saleService.cancel(id)
      if (response.success) {
        alert('Pré-venda cancelada com sucesso!')
        loadPreVendas()
        if (detailModalOpen) {
          setDetailModalOpen(false)
        }
      }
    } catch (error) {
      console.error('Erro ao cancelar pré-venda:', error)
      alert(error.response?.data?.message || 'Erro ao cancelar pré-venda')
    }
  }

  const handleConvertToSale = async (preVenda) => {
    if (!window.confirm('Deseja converter esta pré-venda em venda normal?\n\n⚠️ ATENÇÃO: A venda normal alterará o estoque ao ser confirmada.')) {
      return
    }

    try {
      // Criar nova venda baseada na pré-venda
      const saleData = {
        type: 'sale',
        customerId: preVenda.customer_id,
        saleDate: preVenda.sale_date,
        totalAmount: preVenda.total_amount,
        discountAmount: preVenda.discount_amount,
        taxAmount: preVenda.tax_amount,
        finalAmount: preVenda.final_amount,
        status: 'draft',
        observation: `Convertida da pré-venda #${preVenda.number}`
      }

      const response = await saleService.create(saleData)
      
      if (response.success) {
        // Copiar itens da pré-venda
        if (selectedPreVenda?.items) {
          for (const item of selectedPreVenda.items) {
            await saleService.addItem(response.data.id, {
              productId: item.product_id,
              quantity: item.quantity,
              unitPrice: item.unit_price,
              totalPrice: item.total_price
            })
          }
        }

        alert('Pré-venda convertida em venda com sucesso!')
        loadPreVendas()
        setDetailModalOpen(false)
      }
    } catch (error) {
      console.error('Erro ao converter pré-venda:', error)
      alert('Erro ao converter pré-venda. Tente novamente.')
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (date) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const getStatusBadge = (status) => {
    const badges = {
      draft: { label: 'Rascunho', class: 'badge-draft' },
      confirmed: { label: 'Confirmada', class: 'badge-confirmed' },
      cancelled: { label: 'Cancelada', class: 'badge-cancelled' },
      completed: { label: 'Concluída', class: 'badge-completed' }
    }
    const badge = badges[status] || { label: status, class: 'badge-default' }
    return <span className={`status-badge ${badge.class}`}>{badge.label}</span>
  }

  const columns = [
    {
      key: 'number',
      label: 'Número',
      render: (value) => <strong>{value}</strong>
    },
    {
      key: 'customer_name',
      label: 'Cliente',
      render: (value) => value || 'Cliente não informado'
    },
    {
      key: 'sale_date',
      label: 'Data',
      render: (value) => formatDate(value)
    },
    {
      key: 'final_amount',
      label: 'Total',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (_, row) => (
        <div className="table-actions">
          <button
            className="action-btn view"
            onClick={() => handleViewDetails(row)}
            title="Visualizar detalhes"
          >
            <Eye size={16} />
          </button>
          {row.status === 'draft' && (
            <>
              <button
                className="action-btn confirm"
                onClick={() => handleConfirm(row.id)}
                title="Confirmar pré-venda"
              >
                <CheckCircle size={16} />
              </button>
              <button
                className="action-btn cancel"
                onClick={() => handleCancel(row.id)}
                title="Cancelar"
              >
                <X size={16} />
              </button>
            </>
          )}
        </div>
      )
    }
  ]

  return (
    <div className="pre-vendas-page">
      <div className="page-header">
        <div>
          <h1>Relação de Pré-Venda</h1>
          <p>Gerencie as pré-vendas do sistema</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => window.open('/erp/vendas/pre-venda', '_blank')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          Nova Pré-Venda
        </button>
      </div>

      {/* Aviso sobre pré-vendas */}
      <div className="presale-warning">
        <AlertCircle size={20} />
        <div>
          <strong>⚠️ Importante:</strong> Pré-vendas <strong>RESERVAM o estoque</strong> ao serem confirmadas, mas não baixam. 
          O estoque só será baixado quando a pré-venda for convertida em venda.
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div className="filter-group">
          <Search className="filter-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="filter-input"
            placeholder="Buscar por número, cliente ou código de barras..."
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value
              const now = Date.now()
              
              // Registrar timestamp desta entrada
              inputTimesRef.current.push(now)
              // Manter apenas os últimos 20 timestamps
              if (inputTimesRef.current.length > 20) {
                inputTimesRef.current.shift()
              }
              
              setSearchInput(value)
              lastInputTimeRef.current = now
            }}
            onKeyDown={(e) => {
              // Se pressionar Enter, executar busca imediatamente
              if (e.key === 'Enter') {
                e.preventDefault()
                if (searchTimeoutRef.current) {
                  clearTimeout(searchTimeoutRef.current)
                }
                setFilters(prev => ({ ...prev, search: searchInput }))
                setPagination(prev => ({ ...prev, page: 1 }))
              }
              // Se pressionar Escape, limpar campo
              if (e.key === 'Escape') {
                setSearchInput('')
                setFilters(prev => ({ ...prev, search: '' }))
                inputTimesRef.current = []
                if (searchInputRef.current) {
                  searchInputRef.current.focus()
                }
              }
            }}
            autoFocus
          />
        </div>

        <div className="filter-group">
          <Filter className="filter-icon" />
          <select
            className="filter-select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">Todos os status</option>
            <option value="draft">Rascunho</option>
            <option value="confirmed">Confirmada</option>
            <option value="reservation">Reservada</option>
            <option value="completed">Concluída</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>

        <div className="filter-group">
          <User className="filter-icon" />
          <select
            className="filter-select"
            value={filters.customerId}
            onChange={(e) => setFilters({ ...filters, customerId: e.target.value })}
          >
            <option value="">Todos os clientes</option>
            {clientes.map(cliente => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <Calendar className="filter-icon" />
          <input
            type="date"
            className="filter-input"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            placeholder="Data inicial"
          />
        </div>

        <div className="filter-group">
          <Calendar className="filter-icon" />
          <input
            type="date"
            className="filter-input"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            placeholder="Data final"
          />
        </div>

        <button
          className="btn-clear-filters"
          onClick={() => {
            setSearchInput('')
            setFilters({
              search: '',
              status: '',
              customerId: '',
              dateFrom: '',
              dateTo: ''
            })
            inputTimesRef.current = []
            // Focar no campo de pesquisa após limpar
            setTimeout(() => {
              if (searchInputRef.current) {
                searchInputRef.current.focus()
              }
            }, 100)
          }}
        >
          Limpar Filtros
        </button>
      </div>

      {/* Tabela */}
      <div className="table-container">
        <DataTable
          data={preVendas}
          columns={columns}
          loading={loading}
          pagination={pagination.total > 0 ? {
            from: (pagination.page - 1) * pagination.limit + 1,
            to: Math.min(pagination.page * pagination.limit, pagination.total),
            total: pagination.total,
            current: pagination.page,
            totalPages: pagination.totalPages,
            hasPrev: pagination.page > 1,
            hasNext: pagination.page < pagination.totalPages,
            onPrev: () => setPagination({ ...pagination, page: pagination.page - 1 }),
            onNext: () => setPagination({ ...pagination, page: pagination.page + 1 })
          } : null}
        />
      </div>

      {/* Modal de Detalhes */}
      <Modal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={`Pré-Venda #${selectedPreVenda?.number || ''}`}
        size="large"
      >
        {selectedPreVenda && (
          <div className="pre-venda-details">
            <div className="details-section">
              <h3>Informações Gerais</h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Número:</label>
                  <span>{selectedPreVenda.number}</span>
                </div>
                <div className="detail-item">
                  <label>Cliente:</label>
                  <span>{selectedPreVenda.customer_name || 'Cliente não informado'}</span>
                </div>
                <div className="detail-item">
                  <label>Data:</label>
                  <span>{formatDate(selectedPreVenda.sale_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  {getStatusBadge(selectedPreVenda.status)}
                </div>
                <div className="detail-item">
                  <label>Total:</label>
                  <span className="total-amount">{formatCurrency(selectedPreVenda.final_amount)}</span>
                </div>
              </div>
            </div>

            {selectedPreVenda.observation && (
              <div className="details-section">
                <h3>Observações</h3>
                <p>{selectedPreVenda.observation}</p>
              </div>
            )}

            {selectedPreVenda.items && selectedPreVenda.items.length > 0 && (
              <div className="details-section">
                <h3>Itens</h3>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Quantidade</th>
                      <th>Preço Unit.</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPreVenda.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.product_name || `Produto #${item.product_id}`}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unit_price)}</td>
                        <td>{formatCurrency(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="details-actions">
              {selectedPreVenda.status === 'draft' && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleConfirm(selectedPreVenda.id)}
                  >
                    <CheckCircle size={18} />
                    Confirmar Pré-venda
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleConvertToSale(selectedPreVenda)}
                  >
                    <RefreshCw size={18} />
                    Converter para Venda
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleCancel(selectedPreVenda.id)}
                  >
                    <X size={18} />
                    Cancelar
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default PreVendasPage

